import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { X, Download, Smartphone } from 'lucide-react';

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }

    // Check if dismissed recently (7 days)
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowBanner(true), 2000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => { setIsInstalled(true); setShowBanner(false); });

    // Show banner for iOS (no beforeinstallprompt event)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS && !window.navigator.standalone) {
      setTimeout(() => setShowBanner(true), 5000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((result) => {
        if (result.outcome === 'accepted') setShowBanner(false);
        setDeferredPrompt(null);
      });
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-install-dismissed', String(Date.now()));
  };

  if (!showBanner || isInstalled) return null;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '100px',
        left: '16px',
        right: '16px',
        maxWidth: '360px',
        marginLeft: 'auto',
        zIndex: 9999,
        WebkitTapHighlightColor: 'transparent',
      }}
      data-testid="pwa-install-banner"
    >
      <div style={{
        backgroundColor: '#18181B',
        color: 'white',
        borderRadius: '16px',
        padding: '16px',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
        border: '1px solid #3f3f46',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{
            width: '40px', height: '40px', backgroundColor: 'rgba(245,158,11,0.2)',
            borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <Smartphone style={{ width: '20px', height: '20px', color: '#f59e0b' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: '14px' }}>Install ROCH App</div>
            <div style={{ fontSize: '12px', color: '#a1a1aa', marginTop: '2px' }}>
              {isIOS
                ? 'Tap Share icon below, then "Add to Home Screen"'
                : 'Add to your home screen for quick access'}
            </div>
          </div>
          <button
            onClick={handleDismiss}
            onTouchEnd={(e) => { e.preventDefault(); handleDismiss(); }}
            style={{
              background: 'none', border: 'none', color: '#71717a', cursor: 'pointer',
              padding: '8px', margin: '-8px', flexShrink: 0, touchAction: 'manipulation'
            }}
          >
            <X style={{ width: '16px', height: '16px' }} />
          </button>
        </div>
        {!isIOS && deferredPrompt && (
          <button
            onClick={handleInstall}
            onTouchEnd={(e) => { e.preventDefault(); handleInstall(); }}
            data-testid="pwa-install-btn"
            style={{
              width: '100%', marginTop: '12px', backgroundColor: 'white', color: '#18181B',
              border: 'none', borderRadius: '12px', height: '36px', fontSize: '14px',
              fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: '8px', touchAction: 'manipulation',
              WebkitAppearance: 'none',
            }}
          >
            <Download style={{ width: '16px', height: '16px' }} />
            Install App
          </button>
        )}
      </div>
    </div>
  );
}
